/** Artikel bearbeiten (Screen mieter/Artikel-Detail): Foto, Bezeichnung, Preis (Protokoll), Etikett neu drucken, Abholen/Entfernen. */
import { useTx } from "../i18n";
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Button, Notice, Seitenzustand, StatusBadge, TextField } from "../ui";
import { ApiFehler } from "../api/client";
import { formatEuro, parseEuro } from "@shared/geld";
import { formatDatum } from "@shared/zeit";
import { FotoFeld, PreisChips, preisHinweis } from "./Erfassen";
import { STATUS_BADGE } from "./MeineArtikel";
import { fotoUrl, useArtikelAendern, useArtikelStatus, useErfassenInfo, useFotoHochladen, useFotoLoeschen, useMeinArtikel } from "./artikel-api";

export function ArtikelDetail() {
  const tx = useTx();
  const { id = "" } = useParams();
  const q = useMeinArtikel(id);
  const info = useErfassenInfo();
  const aendern = useArtikelAendern();
  const status = useArtikelStatus();
  const fotoHoch = useFotoHochladen();
  const fotoWeg = useFotoLoeschen();
  const navigate = useNavigate();
  const [form, setForm] = useState<{ bezeichnung: string; preis: string; kategorie: string; groesse: string } | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [erfolg, setErfolg] = useState<string | null>(null);

  return (
    <Seitenzustand query={q} lines={5}>
      {({ artikel: a, preisverlauf }) => {
        const f = form ?? { bezeichnung: a.bezeichnung, preis: (a.preisCent / 100).toFixed(2).replace(".", ","), kategorie: a.kategorie, groesse: a.groesse };
        const preisCent = parseEuro(f.preis);
        const bearbeitbar = ["im_regal", "rueckgabe_kiste", "abgeholt"].includes(a.status);
        const b = STATUS_BADGE[a.status];
        async function speichern(e: FormEvent) {
          e.preventDefault();
          setFehler(null);
          setErfolg(null);
          if (!preisCent || preisCent <= 0) return setFehler("Bitte einen Preis über 0,00 € eingeben.");
          try {
            await aendern.mutateAsync({ id: a.id, bezeichnung: f.bezeichnung.trim(), preisCent, kategorie: f.kategorie.trim(), groesse: f.groesse.trim() });
            setForm(null);
            setErfolg(preisCent !== a.preisCent ? "Gespeichert. Der Preis hat sich geändert – bitte ein neues Etikett drucken." : "Gespeichert.");
          } catch (err) {
            setFehler(err instanceof ApiFehler ? err.message : "Speichern hat nicht geklappt.");
          }
        }
        return (
          <form className="sc-col" style={{ gap: "var(--space-4)" }} onSubmit={(e) => void speichern(e)} noValidate>
            <div className="sc-row">
              <Link to="/mieter/artikel" className="rg-btn rg-btn--quiet" aria-label="Zurück">‹</Link>
              <h1 className="title">{tx("Artikel bearbeiten")}</h1>
            </div>
            <div className="sc-spread">
              <span className="app-code sc-strong">{a.code}</span>
              <StatusBadge status={b.status}>{b.text}{a.status === "verkauft" ? ` ${formatDatum(a.statusAt)}` : ""}</StatusBadge>
            </div>
            {erfolg ? <Notice tone="success">{erfolg}</Notice> : null}
            {fehler ? <Notice tone="error">{fehler}</Notice> : null}
            {!bearbeitbar ? <Notice tone="info">{a.status === "verkauft" ? "Verkaufte Artikel lassen sich nicht mehr ändern." : "Dieser Artikel liegt gerade an der Kasse."}</Notice> : null}
            <FotoFeld
              vorschau={fotoUrl(a.fotoKey) ?? null}
              laeuft={fotoHoch.isPending}
              onDatei={(blob) => fotoHoch.mutate({ id: a.id, blob }, { onError: (e) => setFehler(e instanceof ApiFehler ? e.message : "Foto konnte nicht gespeichert werden.") })}
              onEntfernen={a.fotoKey ? () => fotoWeg.mutate(a.id) : undefined}
            />
            <TextField label="Bezeichnung" value={f.bezeichnung} onChange={(e) => setForm({ ...f, bezeichnung: e.target.value })} maxLength={80} disabled={!bearbeitbar} />
            <TextField label="Preis" value={f.preis} onChange={(e) => setForm({ ...f, preis: e.target.value })} inputMode="decimal" suffix="€" disabled={!bearbeitbar} error={info.data ? (preisHinweis(preisCent, info.data.preisMinCent, info.data.preisMaxCent) ?? undefined) : undefined} />
            {bearbeitbar && info.data ? <PreisChips chips={info.data.preisChipsCent} wertCent={preisCent} onWahl={(c) => setForm({ ...f, preis: (c / 100).toFixed(2).replace(".", ",") })} /> : null}
            <div className="sc-form">
              <TextField label="Größe" value={f.groesse} onChange={(e) => setForm({ ...f, groesse: e.target.value })} maxLength={40} disabled={!bearbeitbar} />
              <TextField label="Kategorie" value={f.kategorie} onChange={(e) => setForm({ ...f, kategorie: e.target.value })} maxLength={40} disabled={!bearbeitbar} />
            </div>
            {bearbeitbar ? <Button type="submit" variant="primary" block icon="check" loading={aendern.isPending} disabled={!form}>{tx("Speichern")}</Button> : null}
            <Link to={`/mieter/etiketten?ids=${a.id}`} className="rg-btn rg-btn--secondary rg-btn--block">Etikett neu drucken</Link>
            {preisverlauf.length ? (
              <div className="sc-col" style={{ gap: 4 }}>
                <span className="sc-muted sc-small">Preisänderungen</span>
                {preisverlauf.map((p, i) => <span key={i} className="sc-small">{formatDatum(p.erstelltAt)}: {formatEuro(p.altCent)} → {formatEuro(p.neuCent)}{p.grund ? ` (${p.grund})` : ""}</span>)}
              </div>
            ) : null}
            {bearbeitbar ? (
              <div className="sc-col" style={{ gap: "var(--space-2)", marginTop: "var(--space-2)" }}>
                {a.status !== "abgeholt" ? <Button variant="secondary" block icon="package" loading={status.isPending} onClick={() => status.mutate({ id: a.id, status: "abgeholt" }, { onSuccess: () => navigate("/mieter/artikel") })}>Mitgenommen (abgeholt)</Button> : <Button variant="secondary" block icon="library" loading={status.isPending} onClick={() => status.mutate({ id: a.id, status: "im_regal" })}>Wieder ins Regal</Button>}
                <Button variant="quiet" block icon="trash-2" loading={status.isPending} onClick={() => confirm("Artikel entfernen? Er erscheint dann nicht mehr in deiner Liste.") && status.mutate({ id: a.id, status: "entfernt" }, { onSuccess: () => navigate("/mieter/artikel") })}>Entfernen</Button>
              </div>
            ) : null}
          </form>
        );
      }}
    </Seitenzustand>
  );
}
